import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    handleScroll();
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'How It Works', path: '/how-it-works' },
    { name: 'Pricing', path: '/pricing' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <motion.nav
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-500 ${
        scrolled 
          ? 'bg-gradient-to-r from-purple-900/95 via-indigo-900/95 to-purple-900/95 backdrop-blur-xl shadow-2xl shadow-purple-500/20 border-b border-purple-500/30' 
          : 'bg-gradient-to-r from-transparent via-black/20 to-transparent backdrop-blur-sm'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8 }}
    >
      {/* Minimal Cosmic Glow Effect - Performance Optimized */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-purple-600/3 via-pink-600/3 to-purple-600/3 rounded-lg pointer-events-none"
        animate={isMobile ? {} : {
          opacity: [0.1, 0.2, 0.1],
        }}
        transition={isMobile ? {} : {
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />

      {/* Simplified Stars - Performance First */}
      {!isMobile && (
        <>
          <motion.div
            className="absolute w-1 h-1 bg-yellow-300 rounded-full pointer-events-none"
            style={{
              left: `15%`,
              top: `30%`,
            }}
            animate={{
              opacity: [0.3, 0.8, 0.3],
              scale: [0.8, 1.2, 0.8]
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 0
            }}
          />
          <motion.div
            className="absolute w-0.5 h-0.5 bg-white rounded-full pointer-events-none"
            style={{
              right: `25%`,
              top: `40%`,
            }}
            animate={{
              opacity: [0.2, 0.7, 0.2],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1
            }}
          />
        </>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo with Cosmic Animation */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="flex items-center relative"
          >
            {/* Logo Ring Removed - Clean Design */}
            
            <Link 
              to="/" 
              className="relative z-10 text-xl font-bold"
            >
              <span className="bg-gradient-to-r from-yellow-300 via-yellow-400 to-yellow-300 bg-clip-text text-transparent drop-shadow-lg">
                ✦ Astrohelp ✦
              </span>
            </Link>

            {/* Static Zodiac Symbol - Performance First */}
            <div className="ml-2 text-yellow-400">
              ♈
            </div>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item, index) => (
              <motion.div 
                key={item.name} 
                whileHover={{ scale: 1.05, y: -2 }}
                className="relative"
              >
                {/* Constellation Effect on Hover */}
                <motion.div
                  className="absolute -top-3 -left-2 -right-2 -bottom-3 pointer-events-none"
                  whileHover={{
                    background: "radial-gradient(circle, rgba(234, 179, 8, 0.1) 0%, transparent 70%)",
                  }}
                />
                
                <Link
                  to={item.path}
                  className={`relative font-medium text-sm transition-all duration-300 py-2 px-1 leading-relaxed ${
                    location.pathname === item.path
                      ? 'text-yellow-300 drop-shadow-lg'
                      : 'text-gray-100 hover:text-yellow-300'
                  }`}
                >
                  {item.name}
                  
                  {/* Active Page Indicator */}
                  {location.pathname === item.path && (
                    <motion.div
                      className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-yellow-400 to-transparent"
                      layoutId="activeTab"
                      animate={{
                        boxShadow: [
                          "0 0 5px rgba(234, 179, 8, 0.5)",
                          "0 0 15px rgba(234, 179, 8, 0.8)",
                          "0 0 5px rgba(234, 179, 8, 0.5)",
                        ],
                      }}
                      transition={{
                        boxShadow: { duration: 2, repeat: Infinity },
                      }}
                    />
                  )}
                </Link>

                {/* Floating Stars on Hover */}
                <motion.div
                  className="absolute -top-2 left-1/2 w-1 h-1 bg-yellow-300 rounded-full opacity-0"
                  whileHover={{
                    opacity: [0, 1, 0],
                    y: [-5, -15, -5],
                    scale: [0.5, 1, 0.5],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                  }}
                />
              </motion.div>
            ))}
            
            {/* Book Now Button with Cosmic Effects - Optimized */}
            <motion.button
              onClick={() => navigate('/pricing')}
              whileHover={{ 
                scale: 1.05,
                boxShadow: "0 0 20px rgba(234, 179, 8, 0.4)",
              }}
              whileTap={{ scale: 0.95 }}
              className="relative bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-400 text-black px-5 py-1.5 rounded-full font-medium text-sm transition-all duration-300 shadow-lg overflow-hidden"
            >
              {/* Simplified animated background - lighter effect */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-yellow-300 via-yellow-400 to-yellow-300 opacity-0"
                whileHover={{ opacity: 0.3 }}
                transition={{ duration: 0.3 }}
              />
              
              <span className="relative z-10">🌟 Book Now</span>
            </motion.button>
          </div>

          {/* Mobile menu button - Optimized for performance */}
          <div className="md:hidden relative z-[110]">
            <motion.button
              onClick={() => setIsOpen(!isOpen)}
              whileTap={{ scale: 0.95 }}
              className="relative z-[110] p-3 rounded-md focus:outline-none text-gray-100 hover:text-yellow-300 transition-colors duration-200 bg-purple-900/50 backdrop-blur-sm border border-purple-500/30 touch-manipulation"
              aria-label="Toggle mobile menu"
              style={{ 
                minWidth: '44px', 
                minHeight: '44px',
                WebkitTapHighlightColor: 'transparent' 
              }}
            >
              <motion.svg 
                className="w-6 h-6" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
                animate={{
                  rotate: isOpen ? 90 : 0, // Reduced rotation for smoother animation
                }}
                transition={{ duration: 0.2, ease: "easeInOut" }} // Faster transition
              >
                {isOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </motion.svg>
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation - Optimized for smooth performance */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }} // Faster transition
            className="md:hidden bg-gradient-to-b from-purple-900/98 to-indigo-900/98 backdrop-blur-xl border-t border-purple-500/30 relative z-[100]"
          >
            <div className="px-6 py-6 space-y-4"> {/* Reduced spacing */}
              {navItems.map((item, index) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.03, duration: 0.2 }} // Faster stagger
                  className="relative"
                >
                  <Link
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`block font-medium text-base py-3 px-3 rounded-lg transition-all duration-200 touch-manipulation leading-relaxed ${
                      location.pathname === item.path
                        ? 'text-yellow-300 bg-yellow-300/10'
                        : 'text-gray-100 hover:text-yellow-300 hover:bg-purple-800/30'
                    }`}
                    style={{ 
                      minHeight: '48px',
                      display: 'flex',
                      alignItems: 'center',
                      WebkitTapHighlightColor: 'transparent'
                    }}
                  >
                    {item.name}
                  </Link>
                  
                  {/* Simplified mobile decoration - static for performance */}
                  {index % 3 === 0 && (
                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2 w-1 h-1 bg-yellow-300 rounded-full opacity-60" />
                  )}
                </motion.div>
              ))}
              
              <motion.button
                onClick={() => {
                  navigate('/pricing');
                  setIsOpen(false);
                }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: navItems.length * 0.03, duration: 0.2 }}
                className="w-full bg-gradient-to-r from-yellow-400 to-yellow-500 text-black py-3 rounded-full font-medium text-sm hover:from-yellow-300 hover:to-yellow-400 transition-all duration-200 shadow-lg touch-manipulation"
                style={{ 
                  minHeight: '48px',
                  WebkitTapHighlightColor: 'transparent'
                }}
              >
                🌟 Book Now
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navigation;